export type ParsedPersonnummer =
  | { ok: true; personnummer: string }
  | { ok: false };

const TWELVE_DIGITS = /^(\d{8})-?(\d{4})$/;
const TEN_DIGITS = /^(\d{6})([-+]?)(\d{4})$/;

/** 10 eller 12 siffror, med eller utan skiljetecken. Säger inget om siffrorna. */
export const hasPersonnummerFormat = (input: string) =>
  TWELVE_DIGITS.test(input.trim()) || TEN_DIGITS.test(input.trim());

/**
 * Samma regler som API:t i adecla-back (ADE-50,
 * apps/rapport-generator/lib/validation/personnummer.ts). Ändras den ena ska
 * den andra ändras.
 */
export function parsePersonnummer(
  input: string,
  today: Date = new Date()
): ParsedPersonnummer {
  const trimmed = input.trim();

  let personnummer: string;
  const twelve = TWELVE_DIGITS.exec(trimmed);
  const ten = TEN_DIGITS.exec(trimmed);
  if (twelve) {
    personnummer = twelve[1] + twelve[2];
  } else if (ten) {
    const [, yymmdd, separator, last4] = ten;
    personnummer = withCentury(yymmdd, separator === "+", today) + last4;
  } else {
    return { ok: false };
  }

  const birthDate = personnummer.slice(0, 8);
  if (!isRealDate(birthDate)) return { ok: false };
  if (birthDate > swedishYmd(today)) return { ok: false };
  if (!hasValidCheckDigit(personnummer.slice(2))) return { ok: false };
  return { ok: true, personnummer };
}

/**
 * Samordningsnummer (dag + 60) faller bort här, eftersom BankID kräver ett
 * personnummer.
 */
function isRealDate(yyyymmdd: string): boolean {
  const year = Number(yyyymmdd.slice(0, 4));
  const month = Number(yyyymmdd.slice(4, 6));
  const day = Number(yyyymmdd.slice(6, 8));
  const date = new Date(Date.UTC(year, month - 1, day));
  return (
    date.getUTCFullYear() === year &&
    date.getUTCMonth() === month - 1 &&
    date.getUTCDate() === day
  );
}

/**
 * Skatteverkets regel: utan tecken eller med `-` är personen under 100 år,
 * med `+` 100 år eller äldre.
 */
function withCentury(yymmdd: string, hundredOrOlder: boolean, today: Date) {
  const todayYmd = swedishYmd(today);
  const currentCentury = Math.floor(Number(todayYmd.slice(0, 4)) / 100) * 100;
  let year = currentCentury + Number(yymmdd.slice(0, 2));
  if (`${year}${yymmdd.slice(2)}` > todayYmd) year -= 100;
  if (hundredOrOlder) year -= 100;
  return `${year}${yymmdd.slice(2)}`;
}

/** Dagens datum i Sverige som ÅÅÅÅMMDD, oavsett webbläsarens tidszon. */
function swedishYmd(date: Date): string {
  // sv-SE formaterar som ÅÅÅÅ-MM-DD.
  return date
    .toLocaleDateString("sv-SE", { timeZone: "Europe/Stockholm" })
    .replace(/-/g, "");
}

/** Luhn över de tio siffrorna ÅÅMMDDNNNK. */
function hasValidCheckDigit(tenDigits: string): boolean {
  let sum = 0;
  for (let i = 0; i < 9; i++) {
    const product = Number(tenDigits[i]) * (i % 2 === 0 ? 2 : 1);
    sum += product > 9 ? product - 9 : product;
  }
  return (10 - (sum % 10)) % 10 === Number(tenDigits[9]);
}
